import React from 'react';
import { Target, Award, Users, TrendingUp, Globe2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/mockData';
import { Card } from '../components/ui/card';

const About = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const valueIcons = [Award, TrendingUp, Target, Users];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 to-blue-900/85"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {t.about.title}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            {t.about.subtitle}
          </p>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                <Target className="h-8 w-8 text-blue-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t.about.vision.title}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                {t.about.vision.content}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t.about.values.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.about.values.items.map((value, index) => {
              const Icon = valueIcons[index];
              return (
                <Card
                  key={index}
                  className="p-6 text-center space-y-4 bg-white hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-500 group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-50 rounded-full flex items-center justify-center mx-auto group-hover:from-blue-600 group-hover:to-blue-500 transition-all">
                    <Icon className="h-8 w-8 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{value.name}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              {t.about.company.title}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {t.about.company.items.map((item, index) => (
                <div
                  key={index}
                  className="p-6 rounded-xl bg-gradient-to-br from-blue-50 to-slate-50 border-2 border-gray-200 hover:border-blue-500 transition-colors"
                >
                  <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide">
                    {item.label}
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Expansion Goals */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-slate-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <Globe2 className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-4xl font-bold mb-6">
              {t.about.expansion.title}
            </h2>
            <p className="text-xl text-blue-100 leading-relaxed">
              {t.about.expansion.content}
            </p>
          </div>
        </div>
      </section>

      {/* Images Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            <div className="relative overflow-hidden rounded-2xl shadow-xl group">
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxvZmZpY2UlMjB0ZWFtd29ya3xlbnwwfHx8fDE3NjQ0NTQ0MzV8MA&ixlib=rb-4.1.0&q=85"
                alt="Team Collaboration"
                className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent flex items-end">
                <p className="text-white text-xl font-semibold p-6">
                  {language === 'fr' ? 'Collaboration & Innovation' : 'Collaboration & Innovation'}
                </p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl shadow-xl group">
              <img 
                src="https://images.unsplash.com/photo-1513828583688-c52646db42da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwZXF1aXBtZW50fGVufDB8fHx8MTc2NDQ1NDQ0MXww&ixlib=rb-4.1.0&q=85"
                alt="Operations"
                className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent flex items-end">
                <p className="text-white text-xl font-semibold p-6">
                  {language === 'fr' ? 'Excellence Opérationnelle' : 'Operational Excellence'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Structure */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              {language === 'fr' ? 'Structure Organisationnelle' : 'Organizational Structure'}
            </h2>

            <div className="space-y-6">
              {/* Direction */}
              <Card className="p-6 bg-gradient-to-r from-blue-600 to-blue-500 text-white">
                <h3 className="text-xl font-bold mb-2">
                  {language === 'fr' ? 'Direction Générale' : 'General Management'}
                </h3>
                <p className="text-blue-100">
                  {language === 'fr'
                    ? 'Président Directeur Général, Directeur Général Adjoint'
                    : 'Chief Executive Officer, Deputy General Manager'}
                </p>
              </Card>

              {/* Departments */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="p-6 bg-gradient-to-br from-slate-50 to-blue-50 border-2 border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {language === 'fr' ? 'Département Administratif & Financier' : 'Administrative & Financial Department'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {language === 'fr'
                      ? 'Responsable Administratif et Financier, Comptables'
                      : 'Administrative and Financial Manager, Accountants'}
                  </p>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-blue-50 to-slate-50 border-2 border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {language === 'fr' ? 'Département Commercial' : 'Commercial Department'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {language === 'fr'
                      ? 'Responsable Commercial, Équipes de Vente'
                      : 'Commercial Manager, Sales Teams'}
                  </p>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-slate-50 to-blue-50 border-2 border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {language === 'fr' ? 'Département Technique' : 'Technical Department'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {language === 'fr'
                      ? 'Responsable Technique, Techniciens Spécialisés'
                      : 'Technical Manager, Specialized Technicians'}
                  </p>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-blue-50 to-slate-50 border-2 border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {language === 'fr' ? 'Département Logistique' : 'Logistics Department'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {language === 'fr'
                      ? 'Responsable Logistique, Gestionnaires de Stock'
                      : 'Logistics Manager, Stock Managers'}
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {language === 'fr' ? 'Rejoignez-nous dans notre vision' : 'Join us in our vision'}
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Devenez partenaire de PRESTACO pour vos solutions d\'entreprise'
              : 'Become a PRESTACO partner for your business solutions'}
          </p>
          <a href="/contact">
            <button className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-lg">
              {t.nav.contact}
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;